import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Users, Info, Phone, HelpCircle, Scale, Megaphone, Trophy, Settings, Archive } from 'lucide-react';
import { CustomerServiceQueue } from '../components/admin/CustomerServiceQueue.tsx';
import { TermsAndConditionsManager } from '../components/admin/TermsAndConditionsManager.tsx';
import { FAQsManager } from '../components/admin/FAQsManager.tsx';
import { MarketStandardsManager } from '../components/admin/MarketStandardsManager.tsx';
import { AnnouncementsManager } from '../components/admin/AnnouncementsManager.tsx';
import { CompetitionSettings } from '../components/admin/CompetitionSettings.tsx';
import { CompetitionTermsManager } from '../components/admin/CompetitionTermsManager.tsx';
import { HistoricalCompetitions } from '../components/admin/HistoricalCompetitions.tsx';
// import { useAuth } from '../lib/auth'; // Not needed for UI only

export function AdminManage() {
  // const { user } = useAuth(); // Not needed for UI only
  const [activeFunction, setActiveFunction] = useState<string | null>(null);

  const handleFunctionClick = (functionName: string) => {
    setActiveFunction(functionName);
  };

  // Function Component Placeholder
  const FunctionPlaceholder = ({ title }: { title: string }) => (
    <div className="bg-white p-8 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6">{title}</h2>
      <div className="p-8 bg-gray-100 rounded-lg flex items-center justify-center">
        <p className="text-gray-500">This feature is coming soon.</p>
      </div>
    </div>
  );

  return (
    <div className="py-8">
      <div className="flex items-center gap-4 mb-8">
        <Link to="/admin/dashboard" className="text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <h1 className="text-3xl font-bold">Site Management</h1>
      </div>

      {activeFunction ? (
        <>
          <div className="mb-6">
            <button
              onClick={() => setActiveFunction(null)}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Functions
            </button>
          </div>
          {activeFunction === 'customer-service' && (
            <CustomerServiceQueue />
          )}
          {activeFunction === 'terms' && (
            <TermsAndConditionsManager />
          )}
          {activeFunction === 'about' && (
            <FunctionPlaceholder title="Upload / Update About Us" />
          )}
          {activeFunction === 'contact' && (
            <FunctionPlaceholder title="Upload / Update Contact Details" />
          )}
          {activeFunction === 'faqs' && (
            <FAQsManager />
          )}
          {activeFunction === 'market-standards' && (
            <MarketStandardsManager />
          )}
          {activeFunction === 'announcements' && (
            <AnnouncementsManager />
          )}
          {activeFunction === 'competition-settings' && (
            <CompetitionSettings />
          )}
          {activeFunction === 'competition-terms' && (
            <CompetitionTermsManager />
          )}
          {activeFunction === 'historical-competitions' && (
            <HistoricalCompetitions />
          )}
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Customer Service Moderation Queue */}
          <div 
            className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleFunctionClick('customer-service')}
          >
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold ml-3">Customer Service</h2>
            </div>
            <p className="text-gray-600">Manage customer service requests and user moderation queue.</p>
          </div>

          {/* Terms and Conditions */}
          <div 
            className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleFunctionClick('terms')}
          >
            <div className="flex items-center mb-4">
              <div className="bg-wayja-teal/20 p-3 rounded-full">
                <FileText className="w-6 h-6 text-wayja-teal" />
              </div>
              <h2 className="text-xl font-semibold ml-3">Terms & Conditions</h2>
            </div>
            <p className="text-gray-600">Upload or update the site's terms and conditions.</p>
          </div>

          {/* About Us */}
          <div 
            className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleFunctionClick('about')}
          >
            <div className="flex items-center mb-4">
              <div className="bg-purple-100 p-3 rounded-full">
                <Info className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold ml-3">About Us</h2>
            </div>
            <p className="text-gray-600">Upload or update the About Us page content.</p>
          </div>

          {/* Contact Details */}
          <div 
            className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleFunctionClick('contact')}
          >
            <div className="flex items-center mb-4">
              <div className="bg-green-100 p-3 rounded-full">
                <Phone className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold ml-3">Contact Details</h2>
            </div>
            <p className="text-gray-600">Upload or update contact information for the site.</p>
          </div>

          {/* FAQs */}
          <div 
            className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleFunctionClick('faqs')}
          >
            <div className="flex items-center mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <HelpCircle className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold ml-3">FAQs</h2>
            </div>
            <p className="text-gray-600">Upload or update the Frequently Asked Questions.</p>
          </div>

          {/* Market Standards */}
          <div 
            className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleFunctionClick('market-standards')}
          >
            <div className="flex items-center mb-4">
              <div className="bg-indigo-100 p-3 rounded-full">
                <Scale className="w-6 h-6 text-indigo-600" />
              </div>
              <h2 className="text-xl font-semibold ml-3">Wayja Market Standards</h2>
            </div>
            <p className="text-gray-600">Manage market rules, regulations, and resolution standards.</p>
          </div>

          {/* Announcements */}
          <div 
            className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleFunctionClick('announcements')}
          >
            <div className="flex items-center mb-4">
              <div className="bg-yellow-100 p-3 rounded-full">
                <Megaphone className="w-6 h-6 text-yellow-600" />
              </div>
              <h2 className="text-xl font-semibold ml-3">Announcements</h2>
            </div>
            <p className="text-gray-600">Manage site-wide announcements displayed in the banner at the top of the page.</p>
          </div>

          {/* Competition Settings */}
          <div 
            className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow border-2 border-wayja-teal/30"
            onClick={() => handleFunctionClick('competition-settings')}
          >
            <div className="flex items-center mb-4">
              <div className="bg-wayja-teal/20 p-3 rounded-full">
                <Trophy className="w-6 h-6 text-wayja-teal" />
              </div>
              <h2 className="text-xl font-semibold ml-3">Competition Settings</h2>
            </div>
            <p className="text-gray-600">Configure competition dates, starting balance, leaderboard visibility, and reset wallets.</p>
          </div>

          {/* Competition T&Cs */}
          <div 
            className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow border-2 border-purple-300"
            onClick={() => handleFunctionClick('competition-terms')}
          >
            <div className="flex items-center mb-4">
              <div className="bg-purple-100 p-3 rounded-full">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold ml-3">Competition T&Cs</h2>
            </div>
            <p className="text-gray-600">Upload or update competition-specific terms and conditions.</p>
          </div>

          {/* Historical Competitions */}
          <div 
            className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow border-2 border-gray-300"
            onClick={() => handleFunctionClick('historical-competitions')}
          >
            <div className="flex items-center mb-4">
              <div className="bg-gray-100 p-3 rounded-full">
                <Archive className="w-6 h-6 text-gray-600" />
              </div>
              <h2 className="text-xl font-semibold ml-3">Historical Competitions</h2>
            </div>
            <p className="text-gray-600">View and manage archived competitions for historical reference.</p>
          </div>
        </div>
      )}
    </div>
  );
} 